package com.schoolconnect.app.feature.schoolusers

import androidx.compose.foundation.background
import androidx.compose.foundation.clickable
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.Spacer
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.height
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.material3.Icon
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.text.SpanStyle
import androidx.compose.ui.text.buildAnnotatedString
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.text.withStyle
import androidx.compose.ui.unit.dp
import androidx.compose.ui.unit.sp
import com.schoolconnect.app.designsystem.AppConfirmationDialog
import com.schoolconnect.app.designsystem.AppDanger
import com.schoolconnect.app.designsystem.AppPageHeader
import com.schoolconnect.app.designsystem.AppPageSubtitle
import com.schoolconnect.app.designsystem.AppConfirmationDialogTone
import com.schoolconnect.app.designsystem.AppSuccess
import com.schoolconnect.app.designsystem.list.AdminListFilterBanner
import com.schoolconnect.app.designsystem.list.AdminListPageScaffold
import com.schoolconnect.app.designsystem.list.AdminListMessageAndFilters
import com.schoolconnect.app.designsystem.list.AdminListPrimaryActionButton
import com.schoolconnect.app.designsystem.list.AdminListTableCard
import com.schoolconnect.app.designsystem.list.AdminListTableBody
import com.schoolconnect.app.designsystem.list.AdminListTableShimmer
import com.schoolconnect.app.feature.common.footerText
import com.schoolconnect.app.feature.common.toWindow
import com.schoolconnect.app.feature.common.ShellTopRightActions
import com.schoolconnect.app.feature.common.shellAvatarInitials
import com.schoolconnect.app.feature.common.shellRoleLabel
import com.schoolconnect.app.feature.common.shellRoleSubtitle
import com.schoolconnect.app.feature.schoolusers.add.AddSchoolUserModal
import com.schoolconnect.app.feature.schoolusers.add.addSchoolUserSchools
import com.schoolconnect.app.feature.schoolusers.edit.EditSchoolUserModal
import com.schoolconnect.app.feature.schoolusers.edit.editSchoolUserFormValuesFrom
import ischoolconnect.sharedui.generated.resources.Res
import ischoolconnect.sharedui.generated.resources.circle_check
import ischoolconnect.sharedui.generated.resources.plus
import ischoolconnect.sharedui.generated.resources.trash
import org.jetbrains.compose.resources.painterResource

@Composable
fun SchoolUsersScreen(
    state: SchoolUsersUiState,
    onEvent: (SchoolUsersUiEvent) -> Unit,
    onBack: () -> Unit,
    shellRoleCode: String = "",
    shellSchoolLabel: String = "iConnect.School",
    onChangeRole: () -> Unit = onBack,
    onLogout: () -> Unit = {}
) {
    val pageItems = state.users
    val pageWindow = state.paging.toWindow(pageItems.size)
    var rowsPerPageExpanded by remember { mutableStateOf(false) }
    var openActionMenuRowKey by remember { mutableStateOf<String?>(null) }
    var pendingUserAction by remember { mutableStateOf<PendingSchoolUserAction?>(null) }
    var accountMenuExpanded by remember { mutableStateOf(false) }
    val editUser = state.users.firstOrNull { it.id == state.editingUserId }
    val roleLabel = shellRoleLabel(shellRoleCode)
    val roleSubtitle = shellRoleSubtitle(shellRoleCode)
    val initials = shellAvatarInitials(roleLabel)
    fun schoolUserRowKey(user: SchoolUserRowUi): String =
        user.assignmentId.ifBlank { user.id }

    androidx.compose.runtime.LaunchedEffect(pageItems) {
        if (openActionMenuRowKey != null &&
            pageItems.none { schoolUserRowKey(it) == openActionMenuRowKey }
        ) {
            openActionMenuRowKey = null
        }
    }

    val analytics = state.analytics?.metrics
    val activeUsers = analytics?.activeUsers
        ?: pageItems.count { it.status == SchoolUserStatusUi.ACTIVE }
    val inactiveUsers = analytics?.inactiveUsers
        ?: pageItems.count { it.status != SchoolUserStatusUi.ACTIVE }
    val usersSubtitle = remember(activeUsers, inactiveUsers) {
        buildAnnotatedString {
            append("Create, assign roles, and manage school-level access. ")
            withStyle(SpanStyle(color = AppSuccess, fontWeight = FontWeight.Bold)) {
                append("$activeUsers Active")
            }
            append(" and ")
            withStyle(SpanStyle(color = AppDanger, fontWeight = FontWeight.Bold)) {
                append("$inactiveUsers Inactive")
            }
            append(" users.")
        }
    }

    Box(modifier = Modifier.fillMaxSize()) {
        AdminListPageScaffold(
            title = "School Management and Administration Users",
            subtitle = "Create, assign roles, and manage school-level access.",
            subtitleAnnotated = usersSubtitle,
            breadcrumb = listOf("School Setup", "School Administrators"),
            showSummaryCards = false,
            headerActions = {
                ShellTopRightActions(
                    roleLabel = roleLabel,
                    roleSubtitle = roleSubtitle,
                    initials = initials,
                    expanded = accountMenuExpanded,
                    onExpandedChange = { accountMenuExpanded = it },
                    onChangeRole = onChangeRole,
                    onLogout = onLogout
                )
            },
            backgroundColor = SchoolUsersBackground,
            titleColor = AppPageHeader,
            subtitleColor = AppPageSubtitle,
            summaryCards = {},
            banner = {
                val filterLabels = schoolUsersFilterLabels(state)
                val isUserDialogOpen = state.isAddUserModalOpen || state.isEditUserModalOpen
                val bannerMessage = state.errorMessage.ifBlank { state.infoMessage }
                if (!isUserDialogOpen && bannerMessage.isBlank() && filterLabels.isEmpty()) {
                    AdminListFilterBanner(
                        chips = emptyList(),
                        onClearAll = {}
                    )
                } else if (!isUserDialogOpen) {
                    AdminListMessageAndFilters(
                        message = state.errorMessage.ifBlank { state.infoMessage },
                        onDismissMessage = { onEvent(SchoolUsersUiEvent.ClearMessages) },
                        chips = filterLabels,
                        onClearAll = { onEvent(SchoolUsersUiEvent.FiltersReset) },
                        onRemoveChip = { chip ->
                                when {
                                    chip.startsWith("Search:") -> onEvent(SchoolUsersUiEvent.SearchChanged(""))
                                    chip.startsWith("Status:") -> onEvent(SchoolUsersUiEvent.StatusFilterChanged(SchoolUsersStatusFilter.ALL))
                                    chip.startsWith("School:") -> onEvent(SchoolUsersUiEvent.SchoolFilterChanged(""))
                                    chip.startsWith("Role:") -> onEvent(SchoolUsersUiEvent.RoleFilterChanged(""))
                                    chip.startsWith("Created:") -> {
                                        onEvent(SchoolUsersUiEvent.CreatedFromChanged(""))
                                        onEvent(SchoolUsersUiEvent.CreatedToChanged(""))
                                    }
                                }
                        },
                        onEditFilters = { onEvent(SchoolUsersUiEvent.FilterModalOpened) },
                        messageBackgroundColor = Color(0xFFF3F7FF),
                        messageBorderColor = Color(0xFFD8E4FF),
                        messageContentColor = SchoolUsersPrimary,
                        messageAccentColor = SchoolUsersPrimary,
                        filterBackgroundColor = Color(0xFFF2F7FF),
                        filterBorderColor = SchoolUsersBorder,
                        filterContentColor = SchoolUsersNavy,
                        filterAccentColor = SchoolUsersPrimary,
                        messageLeadingIcon = painterResource(Res.drawable.circle_check)
                    )
                }
            },
            toolbar = {
                SchoolUsersToolbar(
                    searchQuery = state.searchQuery,
                    onSearchQueryChanged = { onEvent(SchoolUsersUiEvent.SearchChanged(it)) },
                    role = state.roleFilter,
                    onRoleSelected = { onEvent(SchoolUsersUiEvent.RoleFilterChanged(it)) },
                    status = state.statusFilter.displayLabel(),
                    onStatusSelected = { onEvent(SchoolUsersUiEvent.StatusFilterChanged(it.toSchoolUsersStatusFilter())) },
                    onFilterClick = { onEvent(SchoolUsersUiEvent.FilterModalOpened) },
                    onResetClick = { onEvent(SchoolUsersUiEvent.ToolbarReset) }
                )
            },
            table = { tableModifier ->
                AdminListTableCard(
                    modifier = tableModifier,
                    surfaceColor = SchoolUsersSurface,
                    borderColor = SchoolUsersBorder,
                    minContentWidth = 1200.dp,
                    header = {
                        SchoolUsersTableHeader(
                            sortField = state.sortField,
                            sortAscending = state.sortAscending,
                            onSortChanged = { onEvent(SchoolUsersUiEvent.SortChanged(it)) }
                        )
                    },
                    body = {
                        if (state.isLoading) {
                            AdminListTableShimmer(
                                columns = schoolUsersTableShimmerColumns(),
                                rowCount = 6,
                                modifier = Modifier.fillMaxWidth().weight(1f),
                                rowHeight = 62.dp,
                                verticalPadding = 0.dp,
                                surfaceColor = SchoolUsersSurface,
                                borderColor = SchoolUsersBorder
                            )
                        } else if (pageItems.isEmpty()) {
                            Box(
                                modifier = Modifier
                                    .fillMaxWidth()
                                    .weight(1f),
                                contentAlignment = Alignment.Center
                            ) {
                                Column(horizontalAlignment = Alignment.CenterHorizontally) {
                                    Text(
                                        text = "No users found",
                                        color = SchoolUsersNavy,
                                        fontWeight = FontWeight.ExtraBold
                                    )
                                    Spacer(modifier = Modifier.height(6.dp))
                                    Text(
                                        text = "Try a different search or filter.",
                                        color = SchoolUsersMuted,
                                        fontWeight = FontWeight.Medium
                                    )
                                }
                            }
                        } else {
                            AdminListTableBody(
                                modifier = Modifier
                                    .fillMaxWidth()
                                    .weight(1f),
                                items = pageItems,
                                key = { it.assignmentId.ifBlank { it.id } }
                            ) { user ->
                                val rowKey = schoolUserRowKey(user)
                                SchoolUsersRow(
                                    user = user,
                                    isActionMenuOpen = openActionMenuRowKey == rowKey,
                                    onActionMenuToggle = {
                                        openActionMenuRowKey =
                                            if (openActionMenuRowKey == rowKey) null else rowKey
                                    },
                                    onActionMenuDismiss = {
                                        if (openActionMenuRowKey == rowKey) {
                                            openActionMenuRowKey = null
                                        }
                                    },
                                    onViewDetails = {
                                        openActionMenuRowKey = null
                                        onEvent(SchoolUsersUiEvent.ViewDetails(user.id))
                                    },
                                    onEditUser = {
                                        openActionMenuRowKey = null
                                        onEvent(SchoolUsersUiEvent.EditUser(user.id))
                                    },
                                    onToggleStatus = {
                                        openActionMenuRowKey = null
                                        pendingUserAction = PendingSchoolUserAction(user, PendingActionType.ToggleStatus)
                                    },
                                    onDeleteUser = {
                                        openActionMenuRowKey = null
                                        pendingUserAction = PendingSchoolUserAction(user, PendingActionType.Delete)
                                    }
                                )
                                }
                        }
                    },
                    footer = {
                        SchoolUsersFooter(
                            pageText = state.paging.footerText(pageItems.size, "users"),
                            pageIndex = state.paging.pageIndex.coerceAtMost(maxOf(0, pageWindow.pageCount - 1)),
                            pageCount = pageWindow.pageCount,
                            onPageChanged = { onEvent(SchoolUsersUiEvent.PageChanged(it)) },
                            pageSize = state.paging.pageSize,
                            onPageSizeChanged = { onEvent(SchoolUsersUiEvent.PageSizeChanged(it)) },
                            rowsPerPageExpanded = rowsPerPageExpanded,
                            onRowsPerPageToggle = { rowsPerPageExpanded = !rowsPerPageExpanded },
                            onRowsPerPageDismiss = { rowsPerPageExpanded = false }
                        )
                    }
                )
            },
            bottomAction = {
                AdminListPrimaryActionButton(
                    label = "Add User",
                    icon = painterResource(Res.drawable.plus),
                    onClick = { onEvent(SchoolUsersUiEvent.AddUserClicked) },
                    backgroundColor = SchoolUsersPrimary
                )
            }
        )

        val viewUser = state.users.firstOrNull { it.id == state.viewingUserId }

        ViewSchoolUserModal(
            open = state.isViewUserModalOpen && viewUser != null,
            user = viewUser,
            onClose = { onEvent(SchoolUsersUiEvent.ViewUserModalClosed) }
        )

        AddSchoolUserModal(
            open = state.isAddUserModalOpen,
            onClose = { onEvent(SchoolUsersUiEvent.AddUserModalClosed) },
            schoolOptions = state.schoolOptions.ifEmpty { addSchoolUserSchools },
            isSaving = state.isAddUserSaving,
            apiError = state.errorMessage,
            onSave = { onEvent(SchoolUsersUiEvent.AddUserSubmitted(it)) }
        )

        SchoolUsersFilterModal(
            open = state.isFilterModalOpen,
            initialValues = SchoolUsersFilterFormValues(
                searchQuery = state.searchQuery,
                schoolId = state.schoolFilterId,
                schoolName = state.schoolFilterName,
                role = state.roleFilter,
                status = if (state.statusFilter == SchoolUsersStatusFilter.ALL) "" else state.statusFilter.name.replace("_", " ").lowercase().replaceFirstChar { it.uppercase() },
                createdFrom = state.createdFrom,
                createdTo = state.createdTo
            ),
                schoolOptions = state.schoolOptions.ifEmpty { addSchoolUserSchools },
            onApply = { values ->
                onEvent(SchoolUsersUiEvent.SearchChanged(values.searchQuery))
                onEvent(SchoolUsersUiEvent.SchoolFilterChanged(values.schoolId, values.schoolName))
                onEvent(SchoolUsersUiEvent.RoleFilterChanged(values.role))
                onEvent(
                    SchoolUsersUiEvent.StatusFilterChanged(
                        when (values.status.lowercase()) {
                            "active" -> SchoolUsersStatusFilter.ACTIVE
                            "inactive", "suspended" -> SchoolUsersStatusFilter.INACTIVE
                            else -> SchoolUsersStatusFilter.ALL
                        }
                    )
                )
                onEvent(SchoolUsersUiEvent.CreatedFromChanged(values.createdFrom))
                onEvent(SchoolUsersUiEvent.CreatedToChanged(values.createdTo))
                onEvent(SchoolUsersUiEvent.FilterModalClosed)
            },
            onClose = { onEvent(SchoolUsersUiEvent.FilterModalClosed) }
        )

        EditSchoolUserModal(
            open = state.isEditUserModalOpen && editUser != null,
            initialValues = editUser?.let(::editSchoolUserFormValuesFrom)
                ?: editSchoolUserFormValuesFrom(
                    SchoolUserRowUi(
                        id = "",
                        name = "",
                        school = "",
                        city = "",
                        mobile = "",
                        email = "",
                        role = SchoolUserRoleUi.SCHOOL_ADMIN,
                        status = SchoolUserStatusUi.ACTIVE
                    )
                ),
            schoolOptions = state.schoolOptions.ifEmpty { addSchoolUserSchools },
            onClose = { onEvent(SchoolUsersUiEvent.EditUserModalClosed) },
            onSave = { values -> editUser?.let { onEvent(SchoolUsersUiEvent.EditUserSubmitted(it.id, values)) } },
            isSaving = state.isEditUserSaving,
            apiError = state.errorMessage
        )

        pendingUserAction?.let { pending ->
            val user = pending.user
            val isActivate = user.status != SchoolUserStatusUi.ACTIVE
            val actionLabel = if (isActivate) "Activate" else "Inactivate"
            AppConfirmationDialog(
                onDismiss = { pendingUserAction = null },
                title = when (pending.action) {
                    PendingActionType.Delete -> "Delete School User"
                    PendingActionType.ToggleStatus -> "$actionLabel School User"
                },
                subtitle = when (pending.action) {
                    PendingActionType.Delete -> "This will move the school user to inactive records."
                    PendingActionType.ToggleStatus -> if (isActivate) {
                        "This will restore the school user to active records."
                    } else {
                        "This will move the school user to inactive records."
                    }
                },
                bodyText = when (pending.action) {
                    PendingActionType.Delete -> "Delete ${user.name}?"
                    PendingActionType.ToggleStatus -> "$actionLabel ${user.name}?"
                },
                confirmLabel = when (pending.action) {
                    PendingActionType.Delete -> "Delete"
                    PendingActionType.ToggleStatus -> actionLabel
                },
                onConfirm = {
                    when (pending.action) {
                        PendingActionType.Delete -> onEvent(SchoolUsersUiEvent.DeleteUser(user.id))
                        PendingActionType.ToggleStatus -> onEvent(SchoolUsersUiEvent.ToggleStatus(user.id))
                    }
                    pendingUserAction = null
                },
                tone = if (pending.action == PendingActionType.Delete) {
                    AppConfirmationDialogTone.Danger
                } else {
                    AppConfirmationDialogTone.Primary
                }
            )
        }
    }
}

private enum class PendingActionType {
    Delete,
    ToggleStatus
}

private data class PendingSchoolUserAction(
    val user: SchoolUserRowUi,
    val action: PendingActionType
)

@Composable
private fun SchoolUsersAddButton(
        onClick: () -> Unit
) {
    Row(
        modifier = Modifier
            .height(40.dp)
            .background(SchoolUsersPrimary, RoundedCornerShape(6.dp))
            .padding(horizontal = 16.dp)
            .clickable(onClick = onClick),
        verticalAlignment = Alignment.CenterVertically,
        horizontalArrangement = Arrangement.spacedBy(8.dp)
    ) {
        Icon(
            painter = painterResource(Res.drawable.plus),
            contentDescription = "Add User",
            tint = Color.White
        )
        Text(
            text = "Add User",
            color = Color.White,
            fontWeight = FontWeight.ExtraBold
        )
    }
}
